import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock } from 'lucide-react';
interface Notification {
  initials: string;
  name: string;
  product: 'iman' | 'acelerado';
  time: string;
}
const NOTIFICATIONS: Notification[] = [{
  initials: 'MG',
  name: 'Martín G. de Córdoba',
  product: 'iman',
  time: 'hace 2 minutos'
}, {
  initials: 'LP',
  name: 'Lucas P. de Rosario',
  product: 'acelerado',
  time: 'hace 5 minutos'
}, {
  initials: 'RM',
  name: 'Rodrigo M. de Bs. As.',
  product: 'iman',
  time: 'hace 8 minutos'
}, {
  initials: 'FA',
  name: 'Facundo A. de Neuquén',
  product: 'acelerado',
  time: 'hace 11 minutos'
}, {
  initials: 'CR',
  name: 'Carlos R. de Mendoza',
  product: 'iman',
  time: 'hace 14 minutos'
}, {
  initials: 'DM',
  name: 'Diego M. de La Plata',
  product: 'acelerado',
  time: 'hace 17 minutos'
}, {
  initials: 'SC',
  name: 'Sebastián C. de Tucumán',
  product: 'iman',
  time: 'hace 23 minutos'
}, {
  initials: 'PA',
  name: 'Pablo A. de Salta',
  product: 'acelerado',
  time: 'hace 31 minutos'
}];
// Fisher-Yates shuffle for a random, non-repeating cycle
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
const SHOW_MS = 5000;
const HIDE_MS = 8000;
const FIRST_DELAY_MS = 3000;
export function SocialProofToast() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const queueRef = useRef<Notification[]>(shuffle(NOTIFICATIONS));
  const indexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const nextNotification = (): Notification => {
    if (indexRef.current >= queueRef.current.length) {
      queueRef.current = shuffle(NOTIFICATIONS);
      indexRef.current = 0;
    }
    return queueRef.current[indexRef.current++];
  };
  useEffect(() => {
    if (dismissed) return;
    let active = true;
    const showNext = () => {
      if (!active) return;
      setCurrent(nextNotification());
      timerRef.current = setTimeout(() => {
        if (!active) return;
        setCurrent(null);
        timerRef.current = setTimeout(showNext, HIDE_MS);
      }, SHOW_MS);
    };
    timerRef.current = setTimeout(showNext, FIRST_DELAY_MS);
    return () => {
      active = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [dismissed]);
  const handleClose = () => {
    setCurrent(null);
    setDismissed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  return <div className="fixed bottom-20 left-4 md:bottom-6 md:left-6 z-[9999] pointer-events-none" data-id="element-224">
      <AnimatePresence data-id="element-225">
        {current && <motion.div key={current.name + current.time} initial={{
        opacity: 0,
        x: '-120%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '-120%'
      }} transition={{
        duration: 0.4,
        ease: [0.34, 1.56, 0.64, 1]
      }} role="status" aria-live="polite" className="pointer-events-auto bg-bg-card border border-border-subtle border-l-4 border-l-gold-primary rounded-[10px] px-4 py-3.5 w-[280px] max-w-[calc(100vw-32px)] md:max-w-[280px] shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_0_1px_rgba(201,168,76,0.1)]" data-id="element-226">
            {/* Top row */}
            <div className="flex items-center justify-between mb-3" data-id="element-227">
              <div className="flex items-center gap-2" data-id="element-228">
                <span className="sp-pulse-dot" aria-hidden="true" data-id="element-229" />
                <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-[0.08em] text-[#22C55E]" data-id="element-230">
                  Compra verificada
                </span>
              </div>
              <button onClick={handleClose} aria-label="Cerrar notificación" className="text-text-muted hover:text-text-secondary transition-colors" data-id="element-231">
                <X size={16} data-id="element-232" />
              </button>
            </div>

            {/* Middle row */}
            <div className="flex items-center gap-3" data-id="element-233">
              <div className="w-9 h-9 rounded-full bg-gold-primary flex items-center justify-center flex-shrink-0" data-id="element-234">
                <span className="text-[#0A0A0A] font-bold text-sm" data-id="element-235">
                  {current.initials}
                </span>
              </div>
              <div className="min-w-0" data-id="element-236">
                <p className="text-[12px] md:text-[13px] font-bold text-text-primary truncate" data-id="element-237">
                  {current.name}
                </p>
                <p className="text-[12px] md:text-[13px] text-text-secondary" data-id="element-238">
                  activó{' '}
                  <span className="text-text-gold font-bold" data-id="element-239">
                    {current.product === 'iman' ? 'Efecto Imán™' : 'Código Acelerado™'}
                  </span>{' '}
                  {current.product === 'iman' ? '🔥' : '⚡'}
                </p>
              </div>
            </div>

            {/* Bottom row */}
            <div className="flex items-center gap-1.5 mt-2.5" data-id="element-240">
              <Clock size={10} className="text-text-muted flex-shrink-0" data-id="element-241" />
              <span className="text-[10px] md:text-[11px] text-text-muted italic" data-id="element-242">
                {current.time}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}